import React from 'react';
import { WarehouseNode, Product, DemandGap } from '../../types';
import './DataGrids.css';

interface DemandGapDisplayProps {
  nodes: WarehouseNode[];
  products: Product[];
  demandGaps: DemandGap[];
  onRefresh: () => Promise<void>;
}

const DemandGapDisplay: React.FC<DemandGapDisplayProps> = ({
  nodes,
  products,
  demandGaps,
  onRefresh
}) => {
  const getDemandGap = (nodeId: string, productId: string): DemandGap | undefined => {
    return demandGaps.find(
      gap => gap.node_id === nodeId && gap.product_id === productId
    );
  };

  const getGapStatus = (gap: number): 'surplus' | 'balanced' | 'shortage' => {
    if (gap > 0) return 'shortage';
    if (gap < 0) return 'surplus';
    return 'balanced';
  };

  const formatGap = (gap: number): string => {
    if (gap > 0) return `+${gap}`;
    return gap.toString();
  };

  const totalShortage = demandGaps
    .filter(gap => 
      gap.gap > 0 && 
      nodes.some(node => node.node_id === gap.node_id) &&
      products.some(product => product.product_id === gap.product_id)
    )
    .reduce((sum, gap) => sum + gap.gap, 0);

  const totalSurplus = Math.abs(demandGaps
    .filter(gap => 
      gap.gap < 0 && 
      nodes.some(node => node.node_id === gap.node_id) &&
      products.some(product => product.product_id === gap.product_id)
    )
    .reduce((sum, gap) => sum + gap.gap, 0));

  const nodesWithShortage = demandGaps
    .filter(gap => 
      gap.gap > 0 && 
      nodes.some(node => node.node_id === gap.node_id) &&
      products.some(product => product.product_id === gap.product_id)
    )
    .map(gap => gap.node_id)
    .filter((nodeId, index, arr) => arr.indexOf(nodeId) === index)
    .length;

  const nodesWithSurplus = demandGaps
    .filter(gap => 
      gap.gap < 0 && 
      nodes.some(node => node.node_id === gap.node_id) &&
      products.some(product => product.product_id === gap.product_id)
    )
    .map(gap => gap.node_id)
    .filter((nodeId, index, arr) => arr.indexOf(nodeId) === index)
    .length;

  // Calculate balanced nodes correctly
  // A node is balanced only if ALL its products have zero gaps
  const getBalancedNodesCount = (): number => {
    const nodeGapStatus = new Map<string, boolean>();
    
    // Initialize all nodes as potentially balanced
    nodes.forEach(node => {
      nodeGapStatus.set(node.node_id, true);
    });
    
    // Mark nodes as unbalanced if they have any non-zero gaps
    // Only consider gaps for existing nodes and products
    demandGaps
      .filter(gap => 
        nodes.some(node => node.node_id === gap.node_id) &&
        products.some(product => product.product_id === gap.product_id)
      )
      .forEach(gap => {
        if (gap.gap !== 0) {
          nodeGapStatus.set(gap.node_id, false);
        }
      });
    
    // Count nodes that remain balanced
    return Array.from(nodeGapStatus.values()).filter(isBalanced => isBalanced).length;
  };

  const balancedNodesCount = getBalancedNodesCount();

  return (
    <div className="demand-gap-display">
      <div className="grid-header">
        <h3>Demand Gap Analysis</h3>
        <p>
          Demand gaps show the difference between customer demand and available inventory. 
          Positive values indicate shortages, negative values indicate surplus.
        </p>
        <button className="btn btn-secondary" onClick={onRefresh}>
          Refresh Gaps
        </button>
      </div>

      <div className="summary-cards">
        <div className="summary-card shortage">
          <h4>Total Shortage</h4>
          <div className="value">{totalShortage}</div>
          <div className="subtitle">{nodesWithShortage} nodes affected</div>
        </div>
        
        <div className="summary-card surplus">
          <h4>Total Surplus</h4>
          <div className="value">{totalSurplus}</div>
          <div className="subtitle">{nodesWithSurplus} nodes with excess</div>
        </div>
        
        <div className="summary-card balanced">
          <h4>Balanced Nodes</h4>
          <div className="value">
            {balancedNodesCount}
          </div>
          <div className="subtitle">No gaps</div>
        </div>
      </div>

      <div className="grid-container">
        <table className="data-grid gap-grid">
          <thead>
            <tr>
              <th className="node-header">Warehouse Node</th>
              {products.map(product => (
                <th key={product.product_id} className="product-header">
                  {product.name}
                  <span className="unit">Gap ({product.unit_of_measure})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nodes.map(node => (
              <tr key={node.node_id}>
                <td className="node-cell">
                  <div className="node-info">
                    <strong>{node.name}</strong>
                    <span className="location">{node.location}</span>
                  </div>
                </td>
                {products.map(product => {
                  const gapData = getDemandGap(node.node_id, product.product_id);
                  const gap = gapData?.gap || 0;
                  const status = getGapStatus(gap);
                  const inventoryQty = gapData?.inventory_quantity || 0;
                  const demandQty = gapData?.demand_quantity || 0;

                  return (
                    <td 
                      key={`${node.node_id}-${product.product_id}`} 
                      className={`gap-cell ${status}`}
                    >
                      <div className="gap-info">
                        <div className={`gap-value ${status}`}>
                          {formatGap(gap)}
                        </div>
                        <div className="gap-breakdown">
                          <small>
                            Demand: {demandQty} | Inventory: {inventoryQty}
                          </small>
                        </div>
                        {status === 'shortage' && (
                          <div className="gap-indicator shortage-indicator">
                            ⚠️ Shortage
                          </div>
                        )}
                        {status === 'surplus' && (
                          <div className="gap-indicator surplus-indicator">
                            ✅ Surplus
                          </div>
                        )}
                        {status === 'balanced' && (
                          <div className="gap-indicator balanced-indicator">
                            ⚖️ Balanced
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid-footer">
        <div className="gap-legend">
          <h4>Legend:</h4>
          <div className="legend-items">
            <div className="legend-item">
              <span className="legend-color shortage"></span>
              <strong>Positive values:</strong> Shortage (demand exceeds inventory)
            </div>
            <div className="legend-item">
              <span className="legend-color surplus"></span>
              <strong>Negative values:</strong> Surplus (inventory exceeds demand)
            </div>
            <div className="legend-item">
              <span className="legend-color balanced"></span>
              <strong>Zero values:</strong> Balanced (demand equals inventory)
            </div>
          </div>
        </div>
        
        <div className="optimization-hint">
          <h4>💡 Optimization Opportunity</h4>
          <p>
            Use the optimization engine to strategically allocate purchase orders 
            to minimize these demand gaps across your warehouse network.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DemandGapDisplay;